import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import type { WoundResponse } from '../../services/api';
import WoundTableRow from './WoundTableRow';
import WoundImageLightbox from './WoundImageLightbox';
import { WOUND_PHOTOS_COLUMN_PX } from './WoundThumbnails';
import type { WoundImage } from './useWoundImages';
import { useIsMobile } from '../../hooks/useIsMobile';

interface WoundsTableProps {
  wounds: WoundResponse[];
  /** Photos per wound id; entries appear as each wound's images resolve. */
  woundImages?: Record<string, WoundImage[]>;
  canEdit?: boolean;
  canDiscontinue?: boolean;
  onEdit: (wound: WoundResponse) => void;
  onDelete: (wound: WoundResponse) => void;
  onDiscontinue: (
    woundId: string,
    date: Date,
    reason: string,
    comments: string
  ) => Promise<void>;
  onOpenAssessments: (wound: WoundResponse) => void;
}

const WoundsTable: React.FC<WoundsTableProps> = ({
  wounds,
  woundImages = {},
  canEdit = true,
  canDiscontinue = false,
  onEdit,
  onDelete,
  onDiscontinue,
  onOpenAssessments,
}) => {
  const isMobile = useIsMobile();
  // The gallery pages through one wound's photos, so it needs the wound as well
  // as the position within it.
  const [gallery, setGallery] = useState<{
    woundId: string;
    index: number;
  } | null>(null);
  // Active wounds first, then healed — within each group, most recently created first
  const sorted = [...wounds].sort((a, b) => {
    const aHealed = !!a.discontinued_date;
    const bHealed = !!b.discontinued_date;
    if (aHealed !== bHealed) return aHealed ? 1 : -1;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  // Per-wound props shared by the desktop row and the mobile card.
  const rowProps = (wound: WoundResponse) => ({
    wound,
    assessmentCount: wound.assessment_count ?? 0,
    images: woundImages[wound.id],
    canEdit,
    canDiscontinue,
    onEdit,
    onDelete,
    onDiscontinue,
    onOpenAssessments,
    onPreviewImage: (index: number) => setGallery({ woundId: wound.id, index }),
  });

  const lightbox = (
    <WoundImageLightbox
      images={gallery ? (woundImages[gallery.woundId] ?? []) : []}
      index={gallery?.index ?? null}
      onIndexChange={index =>
        setGallery(current => (current ? { ...current, index } : current))
      }
      onClose={() => setGallery(null)}
    />
  );

  // Below Tailwind's `md` breakpoint the fixed-width table overflows, so render
  // stacked cards instead of a horizontally scrolling table.
  if (isMobile) {
    return (
      <>
        <div className='space-y-3'>
          {sorted.map(wound => (
            <WoundTableRow key={wound.id} variant='card' {...rowProps(wound)} />
          ))}
        </div>
        {lightbox}
      </>
    );
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className='w-[120px]'>Status</TableHead>
            <TableHead>Wound</TableHead>
            <TableHead style={{ width: WOUND_PHOTOS_COLUMN_PX }}>
              Photos
            </TableHead>
            <TableHead className='w-[150px]'>Onset</TableHead>
            <TableHead className='w-[240px]'>Assessments</TableHead>
            <TableHead className='w-[260px]'>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map(wound => (
            <WoundTableRow key={wound.id} {...rowProps(wound)} />
          ))}
        </TableBody>
      </Table>
      {lightbox}
    </>
  );
};

export default WoundsTable;
